import { shortRef, type UIError } from '@redirector/front/lib/errors';
import { AlertCircle, Check, Copy, X } from 'lucide-react';
import { useState } from 'react';

interface InlineErrorProps {
  error: UIError | null;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Inline error banner. Used inside modals and above forms to surface
 * classified backend errors to the user. Render nothing when error is null.
 *
 * When the backend returns a request/trace ref we display the short form
 * (8 hex chars) and provide a one-click button to copy the full 32-char
 * trace id. That full id correlates 1:1 with the server log lines for
 * the failing request — ops can paste it into their log viewer and get
 * the entire trace.
 */
export default function InlineError({ error, onDismiss, className = '' }: InlineErrorProps) {
  const [copied, setCopied] = useState(false);
  if (!error) return null;

  const { requestId } = error;

  const handleCopyRef = async (ref: string) => {
    try {
      await navigator.clipboard.writeText(ref);
      setCopied(true);
      // Revert the icon after a short confirmation window. If the user
      // dismisses the banner before the timer fires, the component
      // unmounts and the timer is dropped harmlessly.
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is best-effort. Silent failure is fine — the short
      // ref is still visible on screen and users can select/copy manually.
    }
  };

  return (
    <div
      role="alert"
      className={`flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-3 rounded ${className}`}
    >
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div>{error.message}</div>
        {requestId && (
          <div className="mt-1 text-xs text-red-300/80 flex items-center gap-1.5">
            <span>ref: </span>
            <code className="font-mono">{shortRef(requestId)}</code>
            <button
              type="button"
              onClick={() => handleCopyRef(requestId)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-red-500/15 text-red-300/90 hover:text-red-200 transition-colors"
              aria-label="Copy full reference id"
              title="Copy full reference id"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  <span>copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>copy</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-red-300 hover:text-red-200 flex-shrink-0"
          aria-label="Dismiss error"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
